import store from '../../store';
import { getReportFieldsFromValues } from './reportFieldService';
import { getAvailableDimensions } from './exclusionsService';

export const benchmarksSpecialDropdowns = [
  {
    key: 'Category',
    snake: 'category',
    value: 'category',
    category: 'Subcategory',
    storedAs: 'selectedCategory',
  },
  {
    key: 'Subcategory',
    snake: 'subcategory',
    value: 'subcategory',
    category: 'Subcategory',
    placeholder: 'Subcategory (Select up to 3)',
    storedAs: 'selectedSubcategories',
  },
];

const getViewAvailableDimensions = (allowedFilters, dimensions, filters, metrics) => {
  const allReportFields = store.state.dashboard.availableReportFields.allReportFields || [];
  const selectedDimensionsObjects = getReportFieldsFromValues(allReportFields, dimensions);
  const selectedFiltersObjects = getReportFieldsFromValues(allReportFields, filters);
  const selectedMetricsObjects = getReportFieldsFromValues(allReportFields, metrics);

  return getAvailableDimensions(
    allowedFilters,
    selectedDimensionsObjects,
    selectedFiltersObjects,
    selectedMetricsObjects,
  );
};

export const allInsightsAllowedFilters = () => {
  const dimensions = store.state.dashboard.availableReportFields.dimensions;

  return dimensions
    .filter(dimension => {
      const { groups } = dimension;
      const isAllowedGroup = groups.includes('experience') || groups.includes('sales');

      return isAllowedGroup && isBaseFilter(dimension);
    });
};

export const benchmarksSpecialValues = () => {
  return new Set(benchmarksSpecialDropdowns.map(c => c.value));
};

export const isBaseFilter = (dim) => {
  return dim.canFilter && dim.value !== 'brandName'; // TODO: brands dropdown outside
};

export const getAllowedFilters = (storedAllowedFilters, view) => {
  switch (view) {
    case 'Sales Insights':
      return storedAllowedFilters.salesInsights;
    case 'Shopper Intelligence':
      return storedAllowedFilters.demographics;
    default:
      return storedAllowedFilters[view.toLowerCase()];
  }
};

export const insightsAllowedFilters = () => {
  const dimensions = store.state.dashboard.availableReportFields.dimensions;

  return dimensions
    .filter(dimension => dimension.groups.includes('experience') && isBaseFilter(dimension));
};

export const salesInsightsAllowedFilters = () => {
  const dimensions = store.state.dashboard.availableReportFields.dimensions;

  return dimensions
    .filter(dimension => dimension.groups.includes('sales') && isBaseFilter(dimension));
};

export const benchmarksAllowedFilters = () => {
  const dimensions = store.state.dashboard.availableReportFields.dimensions;
  const dimValues = [
    'channelType',
    'trafficType',
    'campaignObjectiveType',
    'platformName',
    'cartName',
    'experienceType',
    'campaignName',
    'audience',
    'adUnitType',
  ];

  const dimensionsObj = dimValues
    .map(dim => dimensions.find(dimension => dimension.value === dim));

  return dimensionsObj.slice(0, 3)
    .concat(benchmarksSpecialDropdowns)
    .concat(dimensionsObj.slice(3));
};

export const removeBrandOnlyFilters = (filters) => {
  const onlyBrands = new Set(['campaignName', 'audience', 'adUnitType']);

  return filters.filter(({ key, value }) => !onlyBrands.has(key) && !onlyBrands.has(value));
};

export const removeCategoryOnlyFilters = (filters) => {
  const onlyCategory = benchmarksSpecialValues();

  return filters.filter(({ key, value }) => !onlyCategory.has(key) && !onlyCategory.has(value));
};

export const explorerAllowedFilters = () => {
  const {
    dashboard: { availableReportFields, reportRequest },
    filters: { dashboardSelections },
  } = store.state;

  const dimensions = availableReportFields.dimensions || [];
  const selectedDimensions = reportRequest.dimensions || [];
  const selectedFilters = dashboardSelections.filters?.map(f => f.key) || [];
  const selectedMetrics = reportRequest.metrics || [];

  const availableDimensions = getViewAvailableDimensions(
    dimensions,
    selectedDimensions,
    selectedFilters,
    selectedMetrics,
  );

  return availableDimensions
    .filter(dimension => isBaseFilter(dimension));
};

export const demographicsAllowedFilters = () => {
  const {
    dashboard: { availableReportFields, reportRequest },
    filters: { dashboardSelections },
  } = store.state;

  const selectedDimension = reportRequest.demographicsDimension || [];
  const selectedFilters = dashboardSelections.filters?.map(f => f.key) || [];
  const selectedMetrics = reportRequest.demographicsMetrics || [];

  const availableDimensions = getViewAvailableDimensions(
    allInsightsAllowedFilters(),
    [selectedDimension],
    selectedFilters,
    selectedMetrics,
  );

  return availableDimensions
    .filter(dimension => isBaseFilter(dimension))
    .concat(availableReportFields.demographicsDimensions);
};

export const getSelectedFilters = (
  dashboardFilters,
  availableDimensions,
) => {
  const dimensionsNames = availableDimensions.reduce((acc, dim) => {
    if (!dim.disabled) acc.push(dim.value);

    return acc;
  }, []);

  return dashboardFilters.reduce(
    (acc, filter) => {
      if (dimensionsNames.includes(filter.key)) acc.push(filter);

      return acc;
    },
    [],
  );
};

export const getViewSelectedFilters = (
  activeView,
  storedAllowedFilters,
  dashboardSelectedFilters,
) => {

  if (!activeView) return [];

  const allowedFilters = getAllowedFilters(storedAllowedFilters, activeView);

  return getSelectedFilters(dashboardSelectedFilters, allowedFilters);
};

export const isViewFilterable = (activeView) => {
  const filterableViews = new Set([
    'Benchmarks',
    'Shopper Intelligence',
    'Explorer',
    'Sales Insights',
    'Insights',
  ]);

  return filterableViews.has(activeView);
};

export const addBenchmarksSpecialFilters = (filtersBase = []) => {
  const specialFilters = benchmarksSpecialDropdowns.reduce((acc, specialDropdown) => {
    const value = [].concat(store.state.dashboard[specialDropdown.storedAs]);
    const filterObj = {
      key: specialDropdown.value,
      operator: 'includes',
      value,
    };

    if (value.length > 0) acc.push(filterObj);

    return acc;
  }, []);

  const specialFiltersExcluided = filtersBase
    .filter(f => !benchmarksSpecialValues().has(f.key));

  return specialFiltersExcluided.concat(specialFilters);
};
